import { isDenied } from "@msuite/citadel";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import type { NavigationItemType } from "@msuite/ui/navigation";
import { filterNonNull } from "@msuite/utils/array";
import { useAuth } from "@/hooks/use-auth";
import { useCitadel } from "@/hooks/use-citadel";
import { FILE_INBOX_PATH } from "@/sites/data/file-inbox/path";

type NavigationGroupItem = NavigationItemType | "divider" | string;

/**
 * Drops the separators a hidden entry leaves behind. A "divider" or a section
 * label earns its place only when a real entry follows it before the next
 * separator; otherwise the group ends on a heading over nothing. A divider at
 * the top of the list goes too — there is nothing above it to separate — while
 * a section label may open the list.
 */
function pruneSeparators(items: Array<NavigationGroupItem>) {
	const pruned = items.filter((item, index) => {
		if (typeof item !== "string") return true;
		const following = items.slice(index + 1);
		const nextSeparator = following.findIndex((entry) => typeof entry === "string");
		const section = nextSeparator === -1 ? following : following.slice(0, nextSeparator);
		return section.length > 0;
	});
	while (pruned[0] === "divider") pruned.shift();
	return pruned;
}

export function useNavigation() {
	/** Hooks */
	const { t } = useTranslation();
	const { organization } = useAuth();

	/** Query */
	const { citadel } = useCitadel();

	/** Computed */
	// `isDenied` is the shared three-state guard (`@msuite/citadel`, `src/denials.ts`): an entry
	// disappears only on a definitive denial, so rules still loading — and a refetch that failed
	// with no cached rules left to answer from — keep the navigation as it is rather than
	// emptying it over a transient blip.
	//
	// Gated by subject, and only for the subjects the server enforces somewhere. The service
	// catalogue carries no gate at all, so its entry stays: hiding an entry whose page still
	// answers costs the user a working screen. The wiki is gated now, and its entry follows —
	// every member holds `read Wiki` by standing, so it disappears only where somebody has
	// deliberately taken it away. Protocols are gated, on `read Protocol`,
	// and the entry stays all the same — the base set carries the `own` reach, so everybody has a
	// protocols screen with at least their own records on it. The controlling list is the opposite
	// case: `GET /projects/invoices` is gated on `read Invoice`, a subject the base set holds no
	// rule for, so an ordinary employee reaches nothing there. The fleet is the same shape — every
	// vehicles route is gated on `Vehicle` and the base set holds no rule for it either.
	const stockIsDenied = isDenied(citadel, "read", "Stock");
	const measurementIsDenied = isDenied(citadel, "read", "Measurement");
	const partnerIsDenied = isDenied(citadel, "read", "Partner");
	const invoiceIsDenied = isDenied(citadel, "read", "Invoice");
	const incomingInvoiceIsDenied = isDenied(citadel, "read", "IncomingInvoice");
	// The payment files name every supplier's account; their route asks for `update`.
	const incomingInvoiceIsUneditable = isDenied(citadel, "update", "IncomingInvoice");
	const calculationIsDenied = isDenied(citadel, "read", "Calculation");
	const vehicleIsDenied = isDenied(citadel, "read", "Vehicle");
	const planIsDenied = isDenied(citadel, "read", "Plan");
	const projectIsDenied = isDenied(citadel, "read", "Project");
	const userIsDenied = isDenied(citadel, "read", "User");
	const attendanceIsDenied = isDenied(citadel, "read", "Attendance");
	// Substitutions gate on `read Absence` throughout — a substitution plan belongs to the
	// absence it covers, so the absence rule decides both entries.
	const absenceIsDenied = isDenied(citadel, "read", "Absence");
	const protocolIsDenied = isDenied(citadel, "read", "Protocol");
	const contactIsDenied = isDenied(citadel, "read", "Contact");
	const contactMembersIsDenied = isDenied(citadel, "read", "ContactMembers");
	const wikiIsDenied = isDenied(citadel, "read", "Wiki");

	const blankNavigationItems: Array<NavigationItemType> = [];
	const navigationItems: Array<NavigationItemType> = filterNonNull<NavigationItemType>([
		{
			label: t("navigation.dashboard"),
			to: "/dashboard",
			icon: icon("LayoutDashboard"),
		},
		planIsDenied ? null : { label: t("navigation.planning"), to: "/plan", icon: icon("Hammer") },
		{
			label: t("projects.projects"),
			icon: icon("FolderOpen"),
			cyId: "navigation-projects",
			items: filterNonNull<NavigationGroupItem>([
				projectIsDenied
					? null
					: {
							label: t("projects.projects"),
							to: "/projects",
							icon: icon("FolderOpen"),
							cyId: "navigation-projects-sub",
						},
				// The map draws the same project list the `/projects` screen reads, so it shares
				// the gate.
				projectIsDenied
					? null
					: {
							label: t("maps.map"),
							to: "/projects/map",
							icon: icon("Map"),
							cyId: "navigation-projects-map",
						},
			]),
		},
		{
			icon: icon("Orbit"),
			label: t("navigation.my_space"),
			cyId: "navigation-my-space",
			items: filterNonNull<NavigationGroupItem>([
				attendanceIsDenied
					? null
					: {
							label: t("users.my_attendances"),
							to: "/attendances",
							icon: icon("Timer"),
						},
				absenceIsDenied
					? null
					: {
							label: t("absences.my_absences"),
							to: "/absences",
							icon: icon("ClockFading"),
						},
				"divider",
				protocolIsDenied
					? null
					: {
							label: t("protocols.protocols"),
							to: "/protocols",
							icon: icon("File"),
						},
				measurementIsDenied
					? null
					: {
							label: t("measurements.measurements"),
							to: "/measurements",
							icon: icon("Ruler"),
						},
				calculationIsDenied
					? null
					: {
							label: t("calculations.calculations"),
							to: "/calculations",
							icon: icon("Euro"),
						},
				absenceIsDenied
					? null
					: {
							label: t("substitutions.substitutions"),
							to: "/substitutions",
							icon: icon("ReplaceAll"),
						},
				"divider",
				stockIsDenied
					? null
					: {
							label: t("stock.my_orders"),
							to: "/orders",
							icon: icon("Truck"),
						},
			]),
		},
		{
			label: t("users.users"),
			icon: icon("Users"),
			cyId: "navigation-users",
			items: filterNonNull<NavigationGroupItem>([
				// The colleague list is behind `read User`, which the base role does not carry.
				// The members endpoint it reads stays ungated — every assignee picker and mention
				// list in the product is made of it — so what the rule closes is this page: the
				// roster, the contact columns, and the way into each person's record.
				userIsDenied
					? null
					: {
							label: t("users.users"),
							to: "/users",
							icon: icon("Users"),
							cyId: "navigation-users",
						},
				attendanceIsDenied
					? null
					: {
							label: t("users.attendances"),
							to: "/users/attendances/all",
							icon: icon("Timer"),
							cyId: "users-attendances",
						},
				absenceIsDenied
					? null
					: {
							label: t("absences.absences"),
							to: "/users/absences/all",
							icon: icon("Calendar"),
							cyId: "users-absences",
						},
			]),
		},
		partnerIsDenied
			? null
			: {
					label: t("partners.partners"),
					to: "/partners",
					icon: icon("Replace"),
				},
		{
			label: t("contacts.contacts"),
			icon: icon("BookUser"),
			items: filterNonNull<NavigationGroupItem>([
				contactIsDenied
					? null
					: {
							label: t("contacts.contacts"),
							to: "/contacts",
							icon: icon("BookUser"),
						},
				contactMembersIsDenied
					? null
					: {
							label: t("contacts.members"),
							to: "/contacts/members",
							icon: icon("Users"),
						},
			]),
		},
		{
			label: t("controlling.controlling"),
			icon: icon("Euro"),
			items: filterNonNull<NavigationGroupItem>([
				// The dashboard reads the supplier invoices' figures, so it shares their gate.
				incomingInvoiceIsDenied
					? null
					: {
							label: t("navigation.dashboard"),
							to: "/controlling/dashboard",
							icon: icon("LayoutDashboard"),
						},
				t("controlling.invoices"),
				// What suppliers bill is its own subject, apart from `Invoice`, the organisation's
				// own bills: checking a supplier's invoice and writing one are different jobs.
				incomingInvoiceIsDenied
					? null
					: {
							label: t("controlling.incoming_invoices"),
							to: "/controlling/incoming-invoices",
							icon: icon("FileInput"),
						},
				invoiceIsDenied
					? null
					: {
							label: t("controlling.outgoing_invoices"),
							to: "/controlling/invoices",
							icon: icon("FileOutput"),
						},
				incomingInvoiceIsDenied
					? null
					: {
							label: t("controlling.my_invoice_approvals"),
							to: "/controlling/incoming-invoices/approvals",
							icon: icon("ClipboardCheck"),
						},
				incomingInvoiceIsDenied
					? null
					: {
							label: t("controlling.invoice_reminders"),
							to: "/controlling/incoming-invoices/reminders",
							icon: icon("BellRing"),
						},
				incomingInvoiceIsDenied
					? null
					: {
							label: t("controlling.delivery_notes"),
							to: "/controlling/incoming-invoices/delivery-notes",
							icon: icon("Truck"),
						},
				incomingInvoiceIsUneditable
					? null
					: {
							label: t("controlling.payment_files"),
							to: "/controlling/incoming-invoices/payment-runs",
							icon: icon("Landmark"),
						},
				"divider",
				measurementIsDenied
					? null
					: {
							label: t("measurements.measurements"),
							to: "/controlling/measurements",
							icon: icon("Ruler"),
						},
			]),
		},
		vehicleIsDenied
			? null
			: {
					label: t("vehicles.vehicles"),
					to: "/vehicles",
					icon: icon("Car"),
				},
		stockIsDenied
			? null
			: {
					label: t("stock.stock"),
					icon: icon("Warehouse"),
					cyId: "navigation-stock",
					items: filterNonNull<NavigationGroupItem>([
						// What the organisation stocks, in the order the hierarchy nests: an
						// article hangs off a product type, which hangs off a group.
						t("stock.catalogue"),
						{
							label: t("stock.article_groups"),
							to: "/organization/data/stock/article-groups",
							icon: icon("Layers2"),
						},
						{
							label: t("stock.article_product_types"),
							to: "/organization/data/stock/article-product-types",
							icon: icon("Layers2"),
						},
						{
							label: t("stock.articles"),
							to: "/organization/data/stock/articles",
							icon: icon("Layers2"),
						},
						// What a project asks for and what it sends back: the orders, the
						// templates they are raised from, the tour waves they ship on, and the
						// returns that come the other way.
						t("stock.ordering"),
						{
							label: t("stock.orders"),
							to: "/organization/data/stock/orders",
							icon: icon("Truck"),
						},
						{
							label: t("stock.order_templates"),
							to: "/organization/data/stock/order-templates",
							icon: icon("Layers2"),
						},
						{
							label: t("stock.tours"),
							to: "/organization/data/stock/tour-waves",
							icon: icon("Layers2"),
						},
						{
							label: t("stock.returns"),
							to: "/organization/data/stock/return-orders",
							icon: icon("PackageOpen"),
						},
						// Where material stands and how it moves inside the building: the zones,
						// the carriers it stands on, and one row per hop between two zones.
						t("stock.logistics"),
						{
							label: t("stock.warehouse_zones"),
							to: "/organization/data/stock/warehouse-zones",
							icon: icon("Layers2"),
						},
						{
							label: t("stock.load_carriers"),
							to: "/organization/data/stock/load-carriers",
							icon: icon("Container"),
						},
						{
							label: t("stock.transfer_carriers"),
							to: "/organization/data/stock/transfer-carriers",
							icon: icon("ShoppingCart"),
						},
						{
							label: t("stock.transport_orders"),
							to: "/organization/data/stock/transport-orders",
							icon: icon("Truck"),
						},
					]),
				},
		{
			label: t("organization.data.data"),
			icon: icon("Database"),
			items: filterNonNull<NavigationGroupItem>([
				{
					label: t("organization.data.services"),
					to: "/organization/data/services",
					icon: icon("Server"),
				},
				// Ungated: reading a file asks no rule, so every member reads the inbox.
				{
					label: t("files.inbox"),
					to: FILE_INBOX_PATH,
					icon: icon("Inbox"),
				},
			]),
		},
		wikiIsDenied
			? null
			: {
					label: t("wiki.wiki"),
					to: "/wiki",
					icon: icon("Book"),
				},
	])
		// A group whose entries are all gated away would still render its heading,
		// so it goes with them — and the separators inside a surviving group only
		// keep the sections that still have entries under them.
		.map((item) => (item.items ? { ...item, items: pruneSeparators(item.items) } : item))
		.filter((item) => !item.items || item.items.some((entry) => typeof entry !== "string"));

	/** Guard */
	const items = organization
		? navigationItems.filter((item): item is NavigationItemType => item !== null)
		: blankNavigationItems;

	/** Return */
	return items;
}
